import seoRoutePaths from "./seo-route-paths.json" with { type: "json" };

export const SITE_ORIGIN = "https://permitpilott.com";
export const SITE_NAME = "PermitPilott";
export const DEFAULT_IMAGE = null;

const defaultDescription =
  "Turn oversized load permit PDFs or photos into editable routes. Review waypoints, navigate in-app, or export GPX. Works in all 50 US states.";

export const defaultSeo = {
  title: "Oversized Load Permit Routes & GPX Export | PermitPilott",
  description: defaultDescription,
  canonicalPath: "/",
  robots: "index,follow",
  type: "website",
};

const webApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "PermitPilott",
  url: SITE_ORIGIN,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description: defaultDescription,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Start with 3 free routes. Pro plan for unlimited access.",
  },
};

const pricingSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "PermitPilott",
  description:
    "Oversized load permit reading, PDF-to-route conversion, and custom route planning with in-app navigation, GPX export, voice alerts, and search along route.",
  brand: {
    "@type": "Brand",
    name: "PermitPilott",
  },
  offers: [
    {
      "@type": "Offer",
      name: "Free Trial",
      price: "0",
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
    },
    {
      "@type": "Offer",
      name: "Pro Monthly",
      price: "29.99",
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
    },
    {
      "@type": "Offer",
      name: "Pro Yearly",
      price: "289.99",
      priceCurrency: "USD",
      url: `${SITE_ORIGIN}/pricing`,
    },
  ],
};

export const seoRoutes = {
  [seoRoutePaths.home]: {
    ...defaultSeo,
    structuredData: webApplicationSchema,
  },
  [seoRoutePaths.pricing]: {
    title: "PermitPilott Pricing - Oversized Permit PDF Routes & Route Planner",
    description:
      "Compare PermitPilott plans. Oversized permit reading, route building, in-app navigation, and GPX export — all 50 US states. Start with 3 free routes.",
    canonicalPath: "/pricing",
    robots: "index,follow",
    type: "website",
    structuredData: pricingSchema,
  },
  [seoRoutePaths.terms]: {
    title: "Terms of Service - PermitPilott",
    description:
      "Read the PermitPilott terms of service for using oversized load permit reading, PDF-to-route conversion, route planning, navigation, export, and account features.",
    canonicalPath: "/terms",
    robots: "index,follow",
    type: "article",
  },
  [seoRoutePaths.privacy]: {
    title: "Privacy Policy - PermitPilott",
    description:
      "Learn how PermitPilott handles account, permit, routing, billing, and usage information.",
    canonicalPath: "/privacy",
    robots: "index,follow",
    type: "article",
  },
  [seoRoutePaths.disclaimer]: {
    title: "Routing-Aid Disclaimer - PermitPilott",
    description:
      "PermitPilott is a routing aid for oversized permit directions, not a routing authority. Always verify routes against the issued permit.",
    canonicalPath: "/disclaimer",
    robots: "index,follow",
    type: "article",
  },
};

const noindexRouteTitles = {
  "/login": `Sign in - ${SITE_NAME}`,
  "/signup": `Create Account - ${SITE_NAME}`,
  "/forgot-password": `Forgot Password - ${SITE_NAME}`,
  "/reset-password": `Reset Password - ${SITE_NAME}`,
  "/verify": `Verify Email - ${SITE_NAME}`,
  "/dashboard": `Process Permit - ${SITE_NAME}`,
  "/account": `Account - ${SITE_NAME}`,
  "/routes": `Saved Routes - ${SITE_NAME}`,
  "/planner": `Route Planner - ${SITE_NAME}`,
  "/admin": `Administration - ${SITE_NAME}`,
};

export const noindexRoutes = new Set(Object.keys(noindexRouteTitles));

export const publicSeoPaths = Object.values(seoRoutePaths);

function normalizePath(path) {
  const cleanPath = (path || "/").split("?")[0].split("#")[0];
  if (cleanPath.length > 1 && cleanPath.endsWith("/"))
    return cleanPath.slice(0, -1);
  return cleanPath || "/";
}

export function getSeoForPath(path) {
  const normalizedPath = normalizePath(path);
  if (seoRoutes[normalizedPath]) return seoRoutes[normalizedPath];
  if (noindexRoutes.has(normalizedPath)) {
    return {
      title: noindexRouteTitles[normalizedPath],
      description: defaultDescription,
      canonicalPath: normalizedPath,
      robots: "noindex,nofollow",
      type: "website",
    };
  }
  return {
    ...defaultSeo,
    canonicalPath: normalizedPath,
    robots: "noindex,nofollow",
  };
}

export function canonicalUrl(path) {
  const normalizedPath = normalizePath(path);
  return `${SITE_ORIGIN}${normalizedPath === "/" ? "" : normalizedPath}`;
}

function upsertMetaByName(name, content) {
  upsertMeta("name", name, content);
}

function upsertMetaByProperty(property, content) {
  upsertMeta("property", property, content);
}

function upsertMeta(attribute, value, content) {
  if (!content) return;
  let element = document.head.querySelector(`meta[${attribute}="${value}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

function upsertStructuredData(data) {
  const id = "structured-data";
  let element = document.getElementById(id);
  if (!data) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

export function applyRouteSeo(route, image = DEFAULT_IMAGE) {
  if (typeof document === "undefined") return;

  const seo = getSeoForPath(route.path);
  const url = canonicalUrl(seo.canonicalPath);

  document.title = seo.title;
  upsertMetaByName("description", seo.description);
  upsertMetaByName("robots", seo.robots);
  upsertLink("canonical", url);

  upsertMetaByProperty("og:site_name", SITE_NAME);
  upsertMetaByProperty("og:title", seo.title);
  upsertMetaByProperty("og:description", seo.description);
  upsertMetaByProperty("og:type", seo.type || "website");
  upsertMetaByProperty("og:url", url);

  upsertMetaByName("twitter:card", image ? "summary_large_image" : "summary");
  upsertMetaByName("twitter:title", seo.title);
  upsertMetaByName("twitter:description", seo.description);

  if (image) {
    upsertMetaByProperty("og:image", image);
    upsertMetaByName("twitter:image", image);
  }

  upsertStructuredData(seo.structuredData);
}
